import { motion } from "framer-motion";
import { useState } from "react";
import {
  Phone, Mail, MapPin, Car, Clock, Shield, Star, ChevronDown, ChevronUp,
  CheckCircle, Plane, Building2, ShoppingBag, School, Accessibility,
  Navigation, ArrowRight, Menu, X, MessageCircle
} from "lucide-react";
import { FaApple, FaGooglePlay, FaFacebook, FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";

/* ─── Motion helpers ─── */
const fadeUp = { hidden: { opacity: 0, y: 32 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

/* ─── Data ─── */
const SERVICES = [
  { icon: Accessibility, title: "Wheelchair & WAV", desc: "Fully accessible wheelchair-adapted vehicles for passengers with mobility needs, with trained drivers.", colour: "#D4AF37" },
  { icon: Car, title: "Disabled Badge Holder", desc: "Priority transport for Blue Badge holders across all our operating areas, with door-to-door assistance.", colour: "#D4AF37" },
  { icon: School, title: "School Runs", desc: "Safe, reliable, and punctual school pickups and drop-offs. DBS-checked drivers as standard.", colour: "#D4AF37" },
  { icon: Building2, title: "GP Surgeries & Hospitals", desc: "Medical appointment transport for patients and carers — never miss an important appointment.", colour: "#D4AF37" },
  { icon: ShoppingBag, title: "Shopping Trips", desc: "Comfortable rides to local shopping centres, supermarkets, and retail parks across Dorset.", colour: "#D4AF37" },
  { icon: Navigation, title: "Coach & Bus Stations", desc: "Prompt connections to coach and bus terminals across Bournemouth, Poole, and Wimborne.", colour: "#D4AF37" },
  { icon: Plane, title: "Airport Transfers", desc: "Reliable airport drop-offs and pickups. All major airports covered — fixed fares, no surprises.", colour: "#D4AF37" },
  { icon: Car, title: "Private Hire & Anytime", desc: "Whether it's a night out or a business trip, we're available 24/7 for any occasion.", colour: "#D4AF37" },
];

const AREAS = ["Wimborne", "Poole", "Dorset", "Bournemouth", "Ferndown"];

const TESTIMONIALS = [
  { name: "Margaret T.", location: "Wimborne", rating: 5, text: "Absolutely brilliant service! The driver was on time, the WAV vehicle was spotless, and he helped me in and out with no fuss. I use them every week for my hospital appointments." },
  { name: "David R.", location: "Bournemouth", rating: 5, text: "Best taxi company in the area by far. I booked an airport transfer and the driver arrived 10 minutes early. No hidden charges, great price. Will definitely book again!" },
  { name: "Sandra K.", location: "Poole", rating: 5, text: "My son relies on Wimborne Cab Taxis for his school run. Incredibly reliable, kind drivers, and always a safe journey. Total peace of mind every single day." },
  { name: "James W.", location: "Ferndown", rating: 5, text: "I've used many taxi companies over the years but these guys stand out. Professional, friendly, and always on time. The app booking is super easy too!" },
];

const FAQS = [
  { q: "How do I book a taxi?", a: "You can book via our website booking form, call us on 07757 127214, email info@taxi-bournemouth.com, or use our app (coming soon). We're available 24/7." },
  { q: "Do you have wheelchair accessible vehicles?", a: "Yes! We have a fleet of fully-equipped Wheelchair Accessible Vehicles (WAV) with trained drivers. We cater for all special mobility needs — just let us know when you book." },
  { q: "What areas do you cover?", a: "We cover Wimborne, Poole, Dorset, Bournemouth, Ferndown, and all surrounding areas. We also offer long-distance and airport transfers." },
  { q: "Do you offer fixed fares for airports?", a: "Yes, we offer competitive fixed fares for all airport transfers so there are no surprises. Contact us for a free quote to any UK airport." },
  { q: "Are your drivers DBS checked?", a: "Absolutely. All our drivers are fully licensed, DBS-checked, and trained to provide excellent care — especially for passengers with special needs." },
  { q: "Can I book for school runs?", a: "Yes! We provide safe, reliable school run services. Our drivers are specifically vetted and trained for child transport. Contact us to set up a regular booking." },
  { q: "What is your cancellation policy?", a: "We ask for at least 30 minutes notice for local journeys and 2 hours for airport transfers. Please call us directly for any last-minute changes." },
  { q: "Do you accept card payments?", a: "Yes, we accept cash, card, and contactless payments in-vehicle. Card payments can also be arranged over the phone when booking." },
];

const PRICING = [
  { route: "Local (up to 5 miles)", estimate: "From £6", note: "Wimborne, Poole, Bournemouth town trips" },
  { route: "Medical / Hospital", estimate: "From £10", note: "GP, hospitals, clinics — fixed rates" },
  { route: "Airport Transfer", estimate: "From £55", note: "Bournemouth Airport, Southampton, Heathrow" },
  { route: "School Run (daily)", estimate: "From £8", note: "Per journey, discounts for regular bookings" },
];

/* ─── FAQ Item ─── */
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-[#D4AF3730] rounded-xl overflow-hidden cursor-pointer select-none"
      style={{ background: "oklch(0.15 0.012 43)" }}
      onClick={() => setOpen(o => !o)}
    >
      <div className="flex items-center justify-between px-6 py-4 gap-4">
        <span className="text-white font-medium text-sm md:text-base">{q}</span>
        {open ? <ChevronUp size={18} className="text-[#D4AF37] shrink-0" /> : <ChevronDown size={18} className="text-[#D4AF37] shrink-0" />}
      </div>
      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="px-6 pb-5">
          <p className="text-[#a0a0a0] text-sm leading-relaxed">{a}</p>
        </motion.div>
      )}
    </div>
  );
}

/* ─── Star Rating ─── */
function Stars({ n }: { n: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: n }).map((_, i) => <Star key={i} size={14} fill="#D4AF37" className="text-[#D4AF37]" />)}
    </div>
  );
}

/* ─── Navbar ─── */
function Navbar() {
  const [open, setOpen] = useState(false);
  const navLinks = [
    { label: "Services", href: "#services" },
    { label: "Areas", href: "#areas" },
    { label: "Pricing", href: "#pricing" },
    { label: "Reviews", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];
  const handleNav = (href: string) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-[#D4AF3720]" style={{ background: "rgba(10,10,10,0.95)", backdropFilter: "blur(12px)" }}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)" }}>
            <Car size={18} className="text-black" />
          </div>
          <div>
            <div className="text-white font-bold text-sm leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Wimborne</div>
            <div className="text-[#D4AF37] text-[10px] font-semibold tracking-widest">CAB TAXIS</div>
          </div>
        </div>
        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map(l => (
            <button key={l.label} onClick={() => handleNav(l.href)} className="text-[#a0a0a0] hover:text-[#D4AF37] text-sm transition-colors">{l.label}</button>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:07757127214" className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#c9a228] transition-all">
            <Phone size={14} /> 07757 127214
          </a>
        </div>
        {/* Mobile hamburger */}
        <button className="md:hidden text-white" onClick={() => setOpen(o => !o)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {/* Mobile menu */}
      {open && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden px-4 pb-4 flex flex-col gap-3" style={{ background: "rgba(10,10,10,0.98)" }}>
          {navLinks.map(l => (
            <button key={l.label} onClick={() => handleNav(l.href)} className="text-left text-[#d0d0d0] hover:text-[#D4AF37] py-2 text-sm border-b border-[#ffffff10]">{l.label}</button>
          ))}
          <a href="tel:07757127214" className="flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-4 py-3 rounded-lg text-sm font-bold mt-2">
            <Phone size={14} /> Call 07757 127214
          </a>
        </motion.div>
      )}
    </nav>
  );
}

/* ─── Booking Form ─── */
function BookingForm() {
  const [form, setForm] = useState({ pickup: "", destination: "", date: "", time: "", passengers: "1", service: "" });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Booking request sent! We'll call you to confirm shortly.", { duration: 5000 });
  };
  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
        { key: "pickup", label: "Pickup Location", placeholder: "e.g. Wimborne Town Centre", type: "text" },
        { key: "destination", label: "Destination", placeholder: "e.g. Bournemouth Airport", type: "text" },
        { key: "date", label: "Date", placeholder: "", type: "date" },
        { key: "time", label: "Time", placeholder: "", type: "time" },
      ].map(f => (
        <div key={f.key}>
          <label className="text-[#D4AF37] text-xs font-semibold mb-1.5 block tracking-wider uppercase">{f.label}</label>
          <input
            type={f.type}
            placeholder={f.placeholder}
            value={form[f.key as keyof typeof form]}
            onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
            required
            className="w-full px-4 py-3 rounded-lg bg-[#1a1a1a] border border-[#D4AF3730] text-white placeholder-[#555] focus:outline-none focus:border-[#D4AF37] text-sm transition-all"
          />
        </div>
      ))}
      <div>
        <label className="text-[#D4AF37] text-xs font-semibold mb-1.5 block tracking-wider uppercase">Passengers</label>
        <select value={form.passengers} onChange={e => setForm(p => ({ ...p, passengers: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-[#1a1a1a] border border-[#D4AF3730] text-white focus:outline-none focus:border-[#D4AF37] text-sm">
          {["1", "2", "3", "4", "5", "6+"].map(n => <option key={n}>{n}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[#D4AF37] text-xs font-semibold mb-1.5 block tracking-wider uppercase">Service Type</label>
        <select value={form.service} onChange={e => setForm(p => ({ ...p, service: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-[#1a1a1a] border border-[#D4AF3730] text-white focus:outline-none focus:border-[#D4AF37] text-sm">
          <option value="">Select a service…</option>
          <option>Standard Taxi</option>
          <option>Wheelchair / WAV</option>
          <option>Airport Transfer</option>
          <option>School Run</option>
          <option>Medical / Hospital</option>
          <option>Shopping Trip</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <button type="submit" className="w-full py-4 rounded-xl text-black font-bold text-base tracking-wide flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)", boxShadow: "0 8px 30px -6px rgba(212,175,55,0.45)" }}>
          <Car size={18} /> Book My Ride Now
        </button>
      </div>
    </form>
  );
}

/* ─── MAIN PAGE ─── */
export default function Home() {
  return (
    <div className="min-h-screen" style={{ background: "#0a0a0a", color: "#ffffff" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background gradient orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[120px] opacity-20" style={{ background: "#D4AF37" }} />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-[100px] opacity-10" style={{ background: "#D4AF37" }} />
          {/* Checkerboard accent strip */}
          <div className="absolute bottom-0 left-0 right-0 h-1 opacity-40" style={{ background: "repeating-linear-gradient(90deg, #D4AF37 0px, #D4AF37 12px, #000 12px, #000 24px)" }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          {/* Left copy */}
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 border border-[#D4AF3740] rounded-full px-4 py-1.5 mb-6">
              <div className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
              <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase">Available 24 / 7 · Dorset & Beyond</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl xl:text-7xl font-bold leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <span className="text-white">Wimborne</span><br />
              <span style={{ background: "linear-gradient(90deg, #D4AF37, #f5d870)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Cab Taxis</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-xl text-[#a0a0a0] mb-3 font-medium">
              Ride with Comfort. Arrive with Confidence.
            </motion.p>
            <motion.p variants={fadeUp} className="text-[#707070] text-base leading-relaxed mb-8 max-w-lg">
              Wimborne's most trusted taxi service — serving Wimborne, Poole, Bournemouth, and all of Dorset. Specialist wheelchair, WAV, and accessible transport. No hidden fees. No stress.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4 mb-10">
              <a href="#booking" onClick={e => { e.preventDefault(); document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" }); }} className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-black font-bold text-sm transition-all hover:scale-105 active:scale-95" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)", boxShadow: "0 8px 24px -6px rgba(212,175,55,0.5)" }}>
                <Car size={16} /> Book Your Ride
              </a>
              <a href="tel:07757127214" className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-sm border border-[#D4AF3750] hover:border-[#D4AF37] transition-all hover:bg-[#D4AF3710]">
                <Phone size={16} className="text-[#D4AF37]" /> 07757 127214
              </a>
            </motion.div>
            {/* Trust badges */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-6">
              {[
                { icon: Shield, label: "DBS Checked Drivers" },
                { icon: Clock, label: "24/7 Available" },
                { icon: Accessibility, label: "WAV Specialist" },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-2">
                  <b.icon size={16} className="text-[#D4AF37]" />
                  <span className="text-[#808080] text-xs font-medium">{b.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
          {/* Right — booking card */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" id="booking" className="rounded-2xl p-6 md:p-8 border border-[#D4AF3730]" style={{ background: "oklch(0.12 0.01 43)" }}>
            <h2 className="text-white text-xl font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Book Your Taxi</h2>
            <p className="text-[#707070] text-sm mb-6">Fill in your details and we'll confirm your ride</p>
            <BookingForm />
            <p className="text-center text-[#555] text-xs mt-4">Or call us directly: <a href="tel:07757127214" className="text-[#D4AF37] font-semibold">07757 127214</a></p>
          </motion.div>
        </div>
      </section>

      {/* ── OPERATING AREAS ── */}
      <section id="areas" className="py-10 border-y border-[#ffffff08]" style={{ background: "oklch(0.10 0.008 43)" }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
            <span className="text-[#555] text-xs font-semibold tracking-widest uppercase">Serving</span>
            {AREAS.map((a, i) => (
              <div key={a} className="flex items-center gap-3">
                {i > 0 && <div className="w-1 h-1 rounded-full bg-[#D4AF3740]" />}
                <div className="flex items-center gap-1.5">
                  <MapPin size={12} className="text-[#D4AF37]" />
                  <span className="text-white font-semibold text-sm">{a}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">What We Offer</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Services Built Around You
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#707070] max-w-xl mx-auto text-base leading-relaxed">
              From accessible transport to airport transfers — we have a service to suit every journey and every passenger.
            </motion.p>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(s => (
              <motion.div key={s.title} variants={fadeUp} className="group p-6 rounded-2xl border border-[#D4AF3720] hover:border-[#D4AF3760] transition-all cursor-default hover:-translate-y-1" style={{ background: "oklch(0.13 0.01 43)" }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all group-hover:scale-110" style={{ background: "rgba(212,175,55,0.12)" }}>
                  <s.icon size={22} style={{ color: "#D4AF37" }} />
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{s.title}</h3>
                <p className="text-[#707070] text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-24 border-y border-[#ffffff08]" style={{ background: "oklch(0.10 0.008 43)" }}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">Why Choose Us</motion.p>
              <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Dorset's Most Trusted Taxi Service
              </motion.h2>
              <motion.p variants={fadeUp} className="text-[#707070] text-base leading-relaxed mb-8">
                We've been serving the Wimborne and Dorset community with pride — putting passengers first every time. Whether you have special mobility needs or simply need a reliable ride, we're the team you can count on.
              </motion.p>
              <motion.div variants={stagger} className="space-y-4">
                {[
                  "Fully licensed & insured vehicles",
                  "DBS-checked, professionally trained drivers",
                  "Specialist WAV & wheelchair accessible transport",
                  "Fixed fares — no surge pricing",
                  "Punctual pickups, every time",
                  "Available 24 hours, 7 days a week",
                  "Friendly, patient drivers for elderly & disabled passengers",
                ].map(pt => (
                  <motion.div key={pt} variants={fadeUp} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    <span className="text-[#c0c0c0] text-sm">{pt}</span>
                  </motion.div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
                <a href="tel:07757127214" className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-black font-bold text-sm transition-all hover:scale-105" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)" }}>
                  <Phone size={16} /> Call Now
                </a>
                <a href="mailto:info@taxi-bournemouth.com" className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-sm border border-[#D4AF3750] hover:border-[#D4AF37] transition-all">
                  <Mail size={16} className="text-[#D4AF37]" /> Email Us
                </a>
              </motion.div>
            </div>
            {/* Stats */}
            <motion.div variants={stagger} className="grid grid-cols-2 gap-5">
              {[
                { value: "5,000+", label: "Happy Passengers" },
                { value: "10+", label: "Years Experience" },
                { value: "24/7", label: "Always Available" },
                { value: "100%", label: "DBS Verified" },
              ].map(s => (
                <motion.div key={s.label} variants={fadeUp} className="p-8 rounded-2xl text-center border border-[#D4AF3720]" style={{ background: "oklch(0.14 0.012 43)" }}>
                  <div className="text-4xl font-bold mb-2" style={{ background: "linear-gradient(90deg, #D4AF37, #f5d870)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Cormorant Garamond', serif" }}>{s.value}</div>
                  <div className="text-[#808080] text-sm">{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">Transparent Pricing</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Fare Estimates
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#707070] max-w-xl mx-auto text-base">
              We believe in honest, upfront pricing — no hidden extras, no surge fees. All fares include VAT.
            </motion.p>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {PRICING.map(p => (
              <motion.div key={p.route} variants={fadeUp} className="p-6 rounded-2xl border border-[#D4AF3730] hover:border-[#D4AF3760] transition-all" style={{ background: "oklch(0.13 0.01 43)" }}>
                <div className="text-3xl font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#D4AF37" }}>{p.estimate}</div>
                <div className="text-white font-semibold text-sm mb-2">{p.route}</div>
                <div className="text-[#606060] text-xs leading-relaxed">{p.note}</div>
              </motion.div>
            ))}
          </motion.div>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center text-[#555] text-sm">
            Exact fares depend on distance and vehicle type. <a href="tel:07757127214" className="text-[#D4AF37] font-semibold hover:underline">Call for a free quote →</a>
          </motion.p>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-24 border-y border-[#ffffff08]" style={{ background: "oklch(0.10 0.008 43)" }}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">Customer Reviews</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What Our Passengers Say
            </motion.h2>
            <motion.div variants={fadeUp} className="flex justify-center items-center gap-2">
              <Stars n={5} />
              <span className="text-[#a0a0a0] text-sm">Rated 5.0 / 5 by our customers</span>
            </motion.div>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map(t => (
              <motion.div key={t.name} variants={fadeUp} className="p-6 rounded-2xl border border-[#D4AF3720] hover:border-[#D4AF3750] transition-all" style={{ background: "oklch(0.13 0.01 43)" }}>
                <Stars n={t.rating} />
                <p className="text-[#c0c0c0] text-sm leading-relaxed mt-4 mb-5 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-black text-sm font-bold" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)" }}>{t.name[0]}</div>
                  <div>
                    <div className="text-white text-sm font-semibold">{t.name}</div>
                    <div className="text-[#555] text-xs">{t.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── APP DOWNLOAD ── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="rounded-3xl p-10 md:p-16 text-center border border-[#D4AF3730] relative overflow-hidden" style={{ background: "oklch(0.13 0.01 43)" }}>
            <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: "radial-gradient(circle at 50% 0%, #D4AF37, transparent 60%)" }} />
            <motion.div variants={fadeUp} className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)" }}>
              <Car size={28} className="text-black" />
            </motion.div>
            <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">Coming Soon</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Our App Is On Its Way
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#707070] mb-8 max-w-lg mx-auto text-base leading-relaxed">
              Book your taxi, track your driver in real time, and manage all your journeys — all from your phone. Available soon on iOS and Android.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              {[
                { icon: FaApple, label: "App Store", sub: "Download on the", store: "ios" },
                { icon: FaGooglePlay, label: "Google Play", sub: "Get it on", store: "android" },
              ].map(app => (
                <button key={app.store} onClick={() => toast.info("Our app is coming soon! Call us to book: 07757 127214")} className="flex items-center gap-3 px-6 py-4 rounded-xl border border-[#D4AF3740] hover:border-[#D4AF37] transition-all group">
                  <app.icon size={24} className="text-[#D4AF37] group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <div className="text-[#808080] text-[10px]">{app.sub}</div>
                    <div className="text-white font-bold text-sm">{app.label}</div>
                  </div>
                </button>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-24 border-t border-[#ffffff08]" style={{ background: "oklch(0.10 0.008 43)" }}>
        <div className="max-w-3xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">Help & Support</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Frequently Asked Questions
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#707070] text-base">Everything you need to know before you book.</motion.p>
          </motion.div>
          <div className="space-y-3">
            {FAQS.map(item => <FaqItem key={item.q} q={item.q} a={item.a} />)}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-3">Get In Touch</motion.p>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ready to Ride?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#707070] max-w-xl mx-auto text-base">We're just a call or message away. Don't hesitate to get in touch.</motion.p>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: Phone, title: "Call or WhatsApp", value: "07757 127214", href: "tel:07757127214", cta: "Call Now" },
              { icon: Mail, title: "Email Us", value: "info@taxi-bournemouth.com", href: "mailto:info@taxi-bournemouth.com", cta: "Send Email" },
              { icon: MapPin, title: "Areas Covered", value: "Wimborne · Poole · Bournemouth · Dorset · Ferndown", href: "#areas", cta: "View Areas" },
            ].map(c => (
              <motion.div key={c.title} variants={fadeUp} className="p-8 rounded-2xl text-center border border-[#D4AF3720] hover:border-[#D4AF3760] transition-all" style={{ background: "oklch(0.13 0.01 43)" }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(212,175,55,0.12)" }}>
                  <c.icon size={22} style={{ color: "#D4AF37" }} />
                </div>
                <div className="text-white font-semibold mb-2">{c.title}</div>
                <div className="text-[#808080] text-sm mb-4 break-all">{c.value}</div>
                <a href={c.href} className="inline-flex items-center gap-1.5 text-[#D4AF37] text-sm font-semibold hover:gap-2.5 transition-all">
                  {c.cta} <ArrowRight size={14} />
                </a>
              </motion.div>
            ))}
          </motion.div>
          {/* Social */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex justify-center gap-4 mt-12">
            {[
              { icon: FaWhatsapp, label: "WhatsApp", href: "https://wa.me/447757127214", colour: "#25D366" },
              { icon: FaFacebook, label: "Facebook", href: "https://facebook.com", colour: "#1877F2" },
              { icon: MessageCircle, label: "SMS", href: "sms:07757127214", colour: "#D4AF37" },
            ].map(s => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#ffffff15] hover:border-[#ffffff30] transition-all hover:-translate-y-0.5" style={{ background: "oklch(0.14 0.012 43)" }}>
                <s.icon size={20} style={{ color: s.colour }} />
              </a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA BANNER ── */}
      <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1a1200, #0a0a0a, #1a1200)" }}>
        <div className="absolute inset-0 pointer-events-none opacity-20" style={{ background: "repeating-linear-gradient(90deg, #D4AF37 0px, #D4AF37 6px, transparent 6px, transparent 30px)" }} />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Your Ride Awaits.<br />
              <span style={{ background: "linear-gradient(90deg, #D4AF37, #f5d870)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Book in 60 Seconds.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#808080] text-base mb-10">
              Professional, accessible, and always on time — across Wimborne, Poole, Bournemouth & Dorset.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <button onClick={() => document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" })} className="flex items-center gap-2 px-8 py-4 rounded-xl text-black font-bold text-base transition-all hover:scale-105 active:scale-95" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)", boxShadow: "0 10px 40px -10px rgba(212,175,55,0.6)" }}>
                <Car size={18} /> Book Your Ride Now
              </button>
              <a href="tel:07757127214" className="flex items-center gap-2 px-8 py-4 rounded-xl text-white font-bold text-base border border-[#D4AF3750] hover:border-[#D4AF37] hover:bg-[#D4AF3710] transition-all">
                <Phone size={18} className="text-[#D4AF37]" /> Call 07757 127214
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-[#ffffff08] py-12" style={{ background: "#050505" }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)" }}>
                  <Car size={18} className="text-black" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Wimborne</div>
                  <div className="text-[#D4AF37] text-[10px] font-semibold tracking-widest">CAB TAXIS</div>
                </div>
              </div>
              <p className="text-[#555] text-xs leading-relaxed max-w-xs">Dorset's trusted taxi and private hire service. Specialist wheelchair, WAV, and accessible transport. Available 24/7.</p>
            </div>
            <div>
              <div className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-4">Quick Links</div>
              <div className="space-y-2">
                {[["Services", "#services"], ["Areas", "#areas"], ["Pricing", "#pricing"], ["Reviews", "#testimonials"], ["FAQ", "#faq"]].map(([label, href]) => (
                  <button key={label} onClick={() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" })} className="block text-[#707070] text-sm hover:text-[#D4AF37] transition-colors text-left">{label}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-4">Contact</div>
              <div className="space-y-2">
                <a href="tel:07757127214" className="flex items-center gap-2 text-[#707070] text-sm hover:text-[#D4AF37] transition-colors"><Phone size={13} /> 07757 127214</a>
                <a href="mailto:info@taxi-bournemouth.com" className="flex items-center gap-2 text-[#707070] text-sm hover:text-[#D4AF37] transition-colors"><Mail size={13} /> info@taxi-bournemouth.com</a>
                <div className="flex items-start gap-2 text-[#707070] text-sm"><MapPin size={13} className="mt-0.5 shrink-0" /> Wimborne · Poole · Bournemouth · Dorset</div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#ffffff08] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[#404040] text-xs">© {new Date().getFullYear()} Wimborne Cab Taxis. All rights reserved.</p>
            <p className="text-[#303030] text-xs">Licensed Private Hire Operator · Dorset</p>
          </div>
        </div>
      </footer>

      {/* ── STICKY MOBILE CTA ── */}
      <div className="fixed bottom-4 left-4 right-4 md:hidden z-40">
        <a href="tel:07757127214" className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-black font-bold text-sm shadow-2xl transition-all active:scale-95" style={{ background: "linear-gradient(135deg, #D4AF37, #b8960c)", boxShadow: "0 8px 30px -6px rgba(212,175,55,0.6)" }}>
          <Phone size={16} /> Tap to Call: 07757 127214
        </a>
      </div>
    </div>
  );
}
